import {
  animate,
  ImageScaleView,
  View,
  ViewOpts,
} from '@play-co/timestep-core/ui';
import Match2LiveOpsProgressbar from 'src/game/minigame/match2-timestep/components/Match2LiveOpsProgresbar';
import { waitForItPromise } from 'src/lib/utils';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import gsap from 'gsap';
import { SuperLollipopIcon } from 'src/game/superLollipop/components/SuperLollipopIcon';

type Match2SuperLollipopStreakLossBarOpts = ViewOpts & {
  currentProgress: number;
  maxProgress: number;
};

const barWidth = 380;
const barScale = 0.82;

export class Match2SuperLollipopStreakLossBar extends View {
  private progressBar: Match2LiveOpsProgressbar;
  private icon: SuperLollipopIcon;
  private currentProgress = 0;
  private breakLoopAnimation = false;
  private lossAmount: LangBitmapFontTextView;
  private xIcon: ImageScaleView;
  private losingFullStreak = false;

  constructor(opts: Match2SuperLollipopStreakLossBarOpts) {
    super({
      width: 350,
      height: 90,
      ...opts,
    });

    this.currentProgress = Math.min(opts.currentProgress, opts.maxProgress);
    this.losingFullStreak = this.currentProgress >= opts.maxProgress;

    // Create main progress bar
    this.progressBar = new Match2LiveOpsProgressbar({
      superview: this,
      width: barWidth,
      height: this.style.height,
      barWidth: barWidth,
      barHeight: 49,
      currentProgress: this.currentProgress,
      maxProgress: opts.maxProgress,
      labelOpts: {
        font: 'Title',
        textShadowOffsetY: 5,
        textShadowOpacity: 0.3,
      },
    });

    this.progressBar.updateOpts({
      scale: barScale,
      x: this.style.width - barWidth * barScale,
      y: this.style.height * 0.5 - this.style.height * barScale * 0.5,
    });

    // Create animating super lollipop icon
    this.icon = new SuperLollipopIcon({
      superview: this,
      x: 32,
      y: this.style.height * 0.5 - 3,
    });

    // Create cross icon
    this.xIcon = new ImageScaleView({
      superview: this.icon,
      image: 'assets/ui/superLollipop/shared/icon_x.png',
      x: 82,
      y: 70,
      width: 50,
      height: 50,
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    // Create floating loss amount text
    this.lossAmount = new LangBitmapFontTextView({
      superview: this,
      size: 40,
      font: 'Title',
      color: '#FF4144',
      localeText: () => `-${this.currentProgress}`,
      width: 60,
      height: 60,
      x: 0,
      y: -5,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      visible: false, // WIll show up with the animation
    });

    // Break the animation loop when the view is removed
    this.on('ViewRemoved', () => {
      this.breakLoopAnimation = true;
    });
  }

  public animate() {
    this.loopAnimation();
  }

  private async loopAnimation() {
    if (this.breakLoopAnimation) return;
    this.progressBar.setProgress(this.currentProgress);
    await waitForItPromise(500);
    this.animateLossAmount();
    await waitForItPromise(500);
    if (this.losingFullStreak) {
      // Animate both bars if losing full streak, to make it more clear
      this.progressBar.greenProgressBar.animateProgress(
        0,
        1500,
        animate.easeOut,
      );
      await waitForItPromise(300);
      await this.progressBar.greyProgressBar.animateProgress(
        0,
        1500,
        animate.easeOut,
      );
    } else {
      // Otherwise just do the regular green bar animation
      await this.progressBar.greenProgressBar.animateProgress(
        0,
        1500,
        animate.easeOut,
      );
    }
    await waitForItPromise(1000);
    this.loopAnimation();
  }

  private async animateLossAmount() {
    const duration = 2;
    const startY = this.progressBar.style.y + 30;
    this.lossAmount.updateOpts({
      x: this.style.width / 2 + 145,
      y: startY,
      opacity: 1,
      scale: 0,
      visible: true,
    });
    gsap.to(this.lossAmount.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });
    gsap.to(this.lossAmount.style, {
      opacity: 0,
      duration: 0.3,
      ease: 'linear',
      delay: duration - 0.3,
    });
    await gsap.to(this.lossAmount.style, {
      y: startY - 60,
      duration: duration,
      ease: 'linear',
    });
  }

  public tick() {
    const now = Date.now();
    this.xIcon.updateOpts({
      scale: 0.8 + Math.sin(now * 0.005) * 0.2,
    });
  }
}
